
"use client"

import { useState, useEffect, Fragment } from "react"
import { Dialog, Transition } from "@headlessui/react"
import {
  X,
  CreditCard,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  User,
  ChevronDown,
  PauseCircle,
  Snowflake,
  Ban,
} from "lucide-react"
import creditAccountsService from "../../services/creditAccountsService"
import clientsService from "../../services/clientsService"
import toast from "react-hot-toast"
import { useCurrentUser } from "../../utils/context/UserProvider"
const statusOptions = [
  { id: 2, name: "Active", value: "active" },
  { id: 3, name: "Inactive", value: "inactive" },
  { id: 5, name: "Suspended", value: "suspended" },
  { id: 6, name: "Frozen", value: "frozen" },
  { id: 7, name: "Blacklisted", value: "blacklisted" },
]

const getStatusBadgeColor = (status) => {
  switch (status) {
    case "active":
      return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"

    case "inactive":
      return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"

    case "frozen":
      return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"

    case "suspended":
      return "bg-white text-red-700 border border-red-300 dark:bg-neutral-900 dark:text-red-400 dark:border-red-500"

    case "blacklisted":
      return "bg-red-700 text-white dark:bg-red-900 dark:text-red-200"

    default:
      return "bg-neutral-100 text-neutral-800 dark:bg-neutral-900/30 dark:text-neutral-400"
  }
}

const CreditAccountFormModal = ({ isOpen, onClose, accountId = null, isEditing = false, onSuccess }) => {
  const [loading, setLoading] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [loadingUsers, setLoadingUsers] = useState(false)

  const { currentUser } = useCurrentUser()

  const [formData, setFormData] = useState({
    account_name: "",
    account_currency: "USD",
    account_id: "",
    status: "active",
    user: "",
  })

  const [availableUsers, setAvailableUsers] = useState([])
  const [showUserDropdown, setShowUserDropdown] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)

  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (isOpen) {
      if (accountId && isEditing) {
        fetchAccountData(accountId)
      } else {
        setFormData({
          account_name: "",
          account_currency: "USD",
          account_id: "",
          status: "active",
          user: "",
        })
        setSelectedUser(null)
        setIsProcessing(false)
        setErrors({})

        if (!isEditing && !["Client"].includes(currentUser?.role?.name)) {
          fetchClients()
        } else if (!isEditing) {
          setFormData((prev) => ({
            ...prev,
            user: currentUser?._id || "",
            account_name: `${currentUser?.reference || currentUser?.fullName || "My"} Credit Account`,
          }))
          setSelectedUser({
            _id: currentUser?._id || "",
            fullName: currentUser?.reference || currentUser?.fullName || "",
            email: currentUser?.email || "",
          })
        }
      }
    }
  }, [isOpen, accountId, isEditing, currentUser])

  const fetchClients = async () => {
    try {
      setLoadingUsers(true)
      const response = await clientsService.searchClients()
      const responseData = response.data

      if (responseData && responseData.statusCode === 200) {

        const clients = responseData.data
          .filter((item) => !item.hasCreditAccount)
          .map((item) => ({
            _id: item._id,
            fullName: item.reference || item.fullName,
            email: item.email,
          }))

        setAvailableUsers(clients)
      }
    } catch (error) {
      console.error("Failed to fetch clients:", error)
      toast.error("Failed to load clients")
    } finally {
      setLoadingUsers(false)
    }
  }

  const fetchAccountData = async (id) => {
    try {
      setLoading(true)
      const response = await creditAccountsService.get(id)

      if (response.data && response.data.statusCode === 200) {
        const account = response.data.data
        setFormData({
          account_name: account.account_name || "",
          account_currency: "USD",
          account_id: account.account_id || "",
          status: account.status || "active",
          user: account.user?._id || "",
        })

        if (account.user) {
          setSelectedUser({
            _id: account.user._id,
            fullName: account.user.reference || account.user.fullName,
            email: account.user.email,
          })
        }
      }
    } catch (error) {
      console.error("Failed to load account data:", error)
      toast.error("Failed to load account data")
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }))
    }
  }

  const handleUserSelect = (user) => {
    setSelectedUser(user)
    setFormData((prev) => ({
      ...prev,
      user: user._id,
      account_name: `${user.reference || user.fullName}'s Credit Account`,
    }))
    setShowUserDropdown(false)

    if (errors.user) {
      setErrors((prev) => ({
        ...prev,
        user: "",
      }))
    }
  }

  const handleStatusChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      status: value,
    }))
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.account_name.trim()) {
      newErrors.account_name = "Account name is required"
    }

    if (!isEditing && !formData.user && !["Client"].includes(currentUser?.role?.name)) {
      newErrors.user = "Please select a user for this account"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) return

    try {
      setIsProcessing(true)

      let response
      if (accountId && isEditing) {
        const { user, ...updateData } = formData
        response = await creditAccountsService.update(accountId, updateData)
      } else {
        response = await creditAccountsService.create(formData)
      }

      if (response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
        toast.success(isEditing ? "Account updated successfully" : "Account created successfully")
        onSuccess?.()
        onClose()
      }
    } catch (error) {
      console.error("Account operation failed:", error)
      toast.error(error.response?.data?.message || "Operation failed")
    } finally {
      setIsProcessing(false)
    }
  }

  const statusButtonSelectedStyles = {
    active: "border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400",
    inactive: "border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400",
    suspended:
      "border-red-500 bg-white dark:bg-neutral-900 text-red-700 dark:text-red-400 dark:border-red-500",
    frozen: "border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400",
    blacklisted: "border-red-700 bg-red-700 text-white dark:bg-red-900 dark:text-red-200",
  }

  const getStatusIcon = (value) => {
    switch (value) {
      case "active":
        return <CheckCircle size={16} className="mr-2" />
      case "inactive":
        return <XCircle size={16} className="mr-2" />
      case "suspended":
        return <PauseCircle size={16} className="mr-2" />
      case "frozen":
        return <Snowflake size={16} className="mr-2" />
      case "blacklisted":
        return <Ban size={16} className="mr-2" />
      default:
        return null
    }
  }

  const currentStatusLabel = statusOptions.find((s) => s.value === formData.status)?.name || formData.status

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-xl bg-white dark:bg-neutral-950 shadow-2xl transition-all border border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30">
                <div className="relative border-b border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 p-5">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-xl font-[400] text-neutral-900 dark:text-white">
                      {isEditing ? "Edit Credit Account" : "Create Credit Account"}
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="p-1.5 rounded-full text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800 transition-colors"
                      type="button"
                      aria-label="Close"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-neutral-500 dark:text-neutral-400" />
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {!isEditing && !["Client"].includes(currentUser?.role?.name) && (
                        <div>
                          <label className="block mb-2 text-sm font-medium text-neutral-800 dark:text-neutral-200">
                            Select Client <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => setShowUserDropdown(!showUserDropdown)}
                              className={`w-full flex items-center justify-between p-2.5 border rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-left transition-colors ${errors.user
                                  ? "border-red-500 dark:border-red-500"
                                  : "border-neutral-200 dark:border-neutral-800 hover:border-blue-300 dark:hover:border-blue-700"
                                }`}
                            >
                              <div className="flex items-center gap-2">
                                <User size={16} className="text-neutral-500 dark:text-neutral-400" />
                                <span className="text-sm text-neutral-900 dark:text-white">
                                  {selectedUser ? selectedUser.reference || selectedUser.fullName : "Select a client"}
                                </span>
                              </div>
                              <ChevronDown size={14} className="text-neutral-400" />
                            </button>

                            {showUserDropdown && (
                              <div className="absolute z-10 mt-1 w-full bg-white dark:border-neutral-900 dark:bg-neutral-900/30 border border-neutral-200 dark:border-neutral-800 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                                {loadingUsers ? (
                                  <div className="p-3 text-center">
                                    <Loader2 size={16} className="animate-spin mx-auto text-neutral-500" />
                                  </div>
                                ) : availableUsers.length > 0 ? (
                                  availableUsers.map((user) => (
                                    <div
                                      key={user._id}
                                      className="p-3 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700 dark:bg-neutral-900 border-b border-neutral-100 dark:border-neutral-800 last:border-0"
                                      onClick={() => handleUserSelect(user)}
                                    >
                                      <div className="flex items-center gap-2">
                                        <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                                          <User size={12} className="text-blue-600 dark:text-blue-400" />
                                        </div>
                                        <div>
                                          <div className="text-sm font-medium text-neutral-900 dark:text-white">
                                            {user.reference || user.fullName}
                                          </div>
                                          <div className="text-xs text-neutral-500 dark:text-neutral-400">{user.email}</div>
                                        </div>
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="p-3 text-center text-neutral-500 dark:text-neutral-400 text-sm">
                                    No clients available
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          {errors.user && (
                            <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                              <AlertCircle size={12} />
                              {errors.user}
                            </p>
                          )}
                        </div>
                      )}

                      {isEditing && selectedUser && (
                        <div className="bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/50 border border-neutral-200 dark:bg-neutral-800 rounded-xl p-3">
                          <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                              <User size={14} className="text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-neutral-900 dark:text-white">
                                {selectedUser.reference || selectedUser.fullName}
                              </div>
                              <div className="text-xs text-neutral-500 dark:text-neutral-400">
                                Account holder • {selectedUser.email}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {!isEditing && !!["Client"].includes(currentUser?.role?.name) && (
                        <div className="bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/50 border border-neutral-200 dark:bg-neutral-800 rounded-xl p-3">
                          <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                              <User size={14} className="text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-neutral-900 dark:text-white">
                                {currentUser?.reference || currentUser?.fullName || "Your Account"}
                              </div>
                              <div className="text-xs text-neutral-500 dark:text-neutral-400">
                                Creating account for yourself • {currentUser?.email}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="block mb-2 text-sm font-medium text-neutral-800 dark:text-neutral-200">
                          Account Name <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            <CreditCard size={16} className="text-neutral-500 dark:text-neutral-400" />
                          </div>
                          <input
                            type="text"
                            name="account_name"
                            value={formData.account_name}
                            onChange={(e) => handleChange("account_name", e.target.value)}
                            className={`bg-white dark:border-neutral-900 dark:bg-neutral-900/30 border ${errors.account_name ? "border-red-500 dark:border-red-500" : "border-neutral-200 dark:bg-neutral-800"
                              } text-neutral-900 dark:text-white text-sm rounded-xl focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5`}
                            placeholder="Enter account name"
                          />
                        </div>
                        {errors.account_name && (
                          <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                            <AlertCircle size={12} />
                            {errors.account_name}
                          </p>
                        )}
                      </div>

                      {/* <div>
                        <label className="block mb-2 text-sm font-medium text-neutral-800 dark:text-neutral-200">
                          Account ID {!isEditing && "(Optional)"}
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            <CreditCard size={16} className="text-neutral-500 dark:text-neutral-400" />
                          </div>
                          <input
                            type="text"
                            name="account_id"
                            value={formData.account_id}
                            onChange={(e) => handleChange("account_id", e.target.value)}
                            className="bg-white dark:border-neutral-900 dark:bg-neutral-900/30 border border-neutral-200 dark:bg-neutral-800 text-neutral-900 dark:text-white text-sm rounded-xl focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5"
                            placeholder={isEditing ? "" : "Enter account ID or leave empty to auto-generate"}
                            disabled={isEditing}
                          />
                        </div>
                        {!isEditing && (
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                            A unique ID will be generated automatically if left empty
                          </p>
                        )}
                      </div> */}

                      <div>
                        <label className="block mb-2 text-sm font-medium text-neutral-800 dark:text-neutral-200">
                          Account Currency
                        </label>
                        <div className="p-2.5 border border-neutral-200 dark:bg-neutral-800 rounded-xl bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/50">
                          <span className="text-sm text-neutral-900 dark:text-white">USD (US Dollar)</span>
                        </div>
                      </div>

                      {/* ✅ Status (editing only, admin/staff only) */}
                      {isEditing && !["Client"].includes(currentUser?.role?.name) && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-neutral-800 dark:text-neutral-200">
                              Account Status
                            </label>

                            <span
                              className={`text-xs px-2.5 py-1 rounded-full font-medium ${getStatusBadgeColor(formData.status)}`}
                            >
                              {currentStatusLabel}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {statusOptions.map((opt) => {
                              const selected = formData.status === opt.value
                              return (
                                <button
                                  key={opt.id}
                                  type="button"
                                  className={`py-2 border rounded-xl text-center transition-colors flex items-center justify-center ${selected
                                      ? statusButtonSelectedStyles[opt.value] || "border-neutral-900"
                                      : "border-neutral-200 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:border-neutral-300 dark:hover:border-neutral-700"
                                    }`}
                                  onClick={() => handleStatusChange(opt.value)}
                                >
                                  {getStatusIcon(opt.value)}
                                  <span className="text-sm">{opt.name}</span>
                                </button>
                              )
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-8 grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      className="h-11 px-4 border border-neutral-200 dark:border-neutral-800 rounded-xl text-neutral-800 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors text-sm font-[400]"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="h-11 px-4 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400 text-sm font-[400] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
                      onClick={handleSave}
                      disabled={
                        isProcessing ||
                        !formData.account_name ||
                        (!isEditing && !formData.user && !["Client"].includes(currentUser?.role?.name))
                      }
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" />
                          {isEditing ? "Updating..." : "Creating..."}
                        </>
                      ) : isEditing ? (
                        "Update Account"
                      ) : (
                        "Create Account"
                      )}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default CreditAccountFormModal
